import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { signIn, signUp } from '@/lib/auth-client';
import { Turnstile } from '@/components/Turnstile';
import { authErrorMessage } from '@/lib/auth-error';
import { AuthLayout, AuthDivider, AuthTextField, GoogleIcon } from '@/components/AuthLayout';
import { Button } from '@/components/ui';

export default function Signup() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  // Two-store pattern for the captcha token:
  //   - state drives UI (disabled-until-solved on Create button)
  //   - ref is read synchronously in onSubmit so we always send the
  //     freshest token, even if the user clicks Create the instant the
  //     captcha resolves (before React's next render).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onCaptchaToken(token: string) {
    captchaRef.current = token;
    setCaptchaToken(token);
  }
  function onCaptchaExpired() {
    captchaRef.current = null;
    setCaptchaToken(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const token = captchaRef.current;
    if (!token) {
      setErr('Please complete the captcha below first.');
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await signUp.email({
      email,
      password,
      name,
      fetchOptions: { headers: { 'x-captcha-response': token } },
    });
    setBusy(false);
    if (error) {
      setErr(authErrorMessage(error, 'Sign-up failed. Try again in a moment.'));
      // A used captcha token can't be reused — clear it so the Turnstile
      // widget knows to refresh (it auto-refreshes on its own, but our
      // submit-button-enabled state needs to reflect "no valid token").
      captchaRef.current = null;
      setCaptchaToken(null);
      return;
    }
    navigate('/app');
  }

  async function onGoogle() {
    setErr(null);
    const { error } = await signIn.social({ provider: 'google', callbackURL: '/app' });
    if (error) setErr(authErrorMessage(error, 'Google sign-in failed.'));
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Free forever. Custom domain included. No credit card."
      topRight={
        <Link
          to="/login"
          className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
        >
          Have an account?
        </Link>
      }
    >
      <Button variant="ghost" fullWidth onClick={onGoogle} leadingIcon={<GoogleIcon />}>
        Continue with Google
      </Button>

      <AuthDivider />

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <AuthTextField
          label="Name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoComplete="name"
        />
        <AuthTextField
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
        <AuthTextField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
        />

        <Turnstile onToken={onCaptchaToken} onExpired={onCaptchaExpired} onError={onCaptchaExpired} />

        {err && <p className="text-sm text-[var(--color-destructive)]">{err}</p>}

        <Button
          type="submit"
          fullWidth
          size="lg"
          disabled={busy || !captchaToken}
          title={!captchaToken ? 'Complete the captcha above first' : undefined}
        >
          {busy ? 'Creating…' : 'Create account'}
        </Button>
        <p className="text-center text-xs text-[var(--color-on-surface-variant)]">
          By signing up you agree to our{' '}
          <Link to="/terms" className="underline">
            Terms
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          .
        </p>
      </form>
    </AuthLayout>
  );
}
